import { Plus, RotateCw, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Input } from "~/components/ui";
import type { DataSourceTemplateRow } from "~/features/dataSourceTemplates/types";
import { DataSourceTemplateFormDialog } from "./DataSourceTemplateFormDialog";
import { DataSourceTemplatePeek } from "./DataSourceTemplatePeek";
import { DataSourceTemplatesTable } from "./DataSourceTemplatesTable";

interface DataSourceTemplatesPresenterProps {
  rows: DataSourceTemplateRow[];
  search: string;
  onSearchChange: (next: string) => void;
  excludeDeactivated: boolean;
  onExcludeDeactivatedChange: (next: boolean) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;
  selectedDataSourceTemplate: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

export function DataSourceTemplatesPresenter({
  rows,
  search,
  onSearchChange,
  excludeDeactivated,
  onExcludeDeactivatedChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  selectedDataSourceTemplate,
  onSelect,
  onCloseSelected,
}: DataSourceTemplatesPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.dataSourceTemplate.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
          <label
            htmlFor="data-source-templates-exclude-deactivated"
            className="inline-flex items-center gap-2 text-14-regular text-fg-2"
          >
            <Checkbox
              id="data-source-templates-exclude-deactivated"
              checked={excludeDeactivated}
              onCheckedChange={(v) => onExcludeDeactivatedChange(v === true)}
            />
            <span>{t("ui.excludeDeactivated")}</span>
          </label>
          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
        </div>
      </div>

      <DataSourceTemplateFormDialog open={createOpen} onOpenChange={setCreateOpen} />

      <DataSourceTemplatesTable
        rows={rows}
        onSelect={onSelect}
        emptyMessage={t("ui.notFound")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <DataSourceTemplatePeek id={selectedDataSourceTemplate} onClose={onCloseSelected} />
    </div>
  );
}
